import * as THREE from 'three/webgpu';

/**
 * @file `BaseLevel` — a working, no-op implementation of the {@link Level}
 * contract from `types.js`, so a generator only writes the parts it cares about
 * and never re-implements dispose/meta bookkeeping (or forgets to).
 *
 * Two ways to use it:
 *
 * ```js
 * // 1. Instantiate and fill in.
 * const level = new BaseLevel({ id: 'forest', seed, size, registry });
 * level.addChunks(chunkGeometries(builder.merge(), builder.bounds(), 3), key => mats[key]);
 * level.colliders = colliders;
 * level.heightAt = field.heightAt;
 * return level.finalize();
 *
 * // 2. Extend, when the level needs an `update()` or custom teardown.
 * class ForestLevel extends BaseLevel { update(dt) { this.sway += dt; } }
 * ```
 *
 * DISPOSE SEMANTICS (idempotent, per the contract):
 *   - removes `root` from its parent and clears it,
 *   - disposes every geometry it created or was told to track,
 *   - disposes the per-level {@link Registry} (materials, textures),
 *   - disposes `decals` / `probes` if they expose `dispose()`.
 * The Engine ALSO disposes the registry after calling this; both are idempotent,
 * so the double call is harmless.
 *
 * @module engine/world/Level
 */

/** @typedef {import('../types.js').Level} Level */
/** @typedef {import('../types.js').Bounds2} Bounds2 */
/** @typedef {import('../types.js').Collider} Collider */
/** @typedef {import('../types.js').LevelMeta} LevelMeta */
/** @typedef {import('../types.js').Registry} Registry */
/** @typedef {import('../types.js').SpawnPoint} SpawnPoint */
/** @typedef {import('./Chunker.js').GeoChunk} GeoChunk */

/** The plan's level-constant sun: ~42 deg elevation, camera-side azimuth. */
const DEFAULT_SUN = { x: 0.42, y: 0.67, z: 0.61 };

/** @implements {Level} */
export class BaseLevel {
  /**
   * @param {Object} [opts]
   * @param {string} [opts.id='level']
   * @param {string} [opts.name]
   * @param {string|number} [opts.seed='default']
   * @param {number} [opts.size=256] Square world extent in metres.
   * @param {Registry} [opts.registry] Per-level resource owner (from the build
   *   ctx). Disposed by {@link dispose}.
   * @param {Bounds2} [opts.bounds] Defaults to a `size`-square centred on origin.
   * @param {{x: number, y: number, z: number}} [opts.sunDir]
   */
  constructor({ id = 'level', name, seed = 'default', size = 256, registry, bounds, sunDir } = {}) {
    const half = size * 0.5;

    /** @type {THREE.Group} */
    this.root = new THREE.Group();
    this.root.name = id;

    /** @type {Collider[]} */
    this.colliders = [];

    /** @type {import('../types.js').DecalLayer|null} */
    this.decals = null;

    /** @type {import('../types.js').LightProbeGrid|null} */
    this.probes = null;

    /**
     * Flat by default. Levels with terrain assign `field.heightAt` (already
     * bound by `Heightfield`). Called per actor per frame — keep it cheap.
     * @type {(x: number, z: number) => number}
     */
    this.heightAt = () => 0;

    /** @type {{player: SpawnPoint, enemies: SpawnPoint[], [k: string]: any}} */
    this.spawnPoints = {
      player: { x: 0, y: 0, z: 0, yaw: 0, tag: 'player' },
      enemies: [],
    };

    /** @type {Bounds2} */
    this.bounds = bounds ?? { minX: -half, minZ: -half, maxX: half, maxZ: half, minY: 0, maxY: 0 };

    /** @type {LevelMeta} */
    this.meta = {
      id,
      name: name ?? id,
      seed,
      size,
      tris: 0,
      drawCalls: 0,
      verts: 0,
      buildMs: 0,
      bakeMs: 0,
      sunDir: sunDir ?? { ...DEFAULT_SUN },
      stats: {},
    };

    /** @type {Registry|null} */
    this.registry = registry ?? null;

    /** @type {Set<{dispose?: () => void}>} Geometries + anything else we own. */
    this._owned = new Set();
    this._disposed = false;
    this._t0 = now();
  }

  // ── construction helpers ───────────────────────────────────────────────────

  /**
   * Registers a disposable this level owns (geometry, batch, worker). Returns it
   * so it can be used inline.
   * @template T
   * @param {T} obj
   * @returns {T}
   */
  own(obj) {
    if (obj) this._owned.add(/** @type {any} */ (obj));
    return obj;
  }

  /**
   * Parents a mesh under `root` and takes ownership of its geometry.
   * @param {THREE.Object3D} obj
   * @returns {THREE.Object3D}
   */
  add(obj) {
    this.root.add(obj);
    const geo = /** @type {any} */ (obj).geometry;
    if (geo?.dispose) this._owned.add(geo);
    return obj;
  }

  /**
   * Turns `chunkGeometries()` output into meshes under `root`.
   * @param {GeoChunk[]} chunks
   * @param {(matKey: string) => THREE.Material} materialFor Resolver — usually
   *   `key => registry.material(key, () => makeMaterial(key))`.
   * @param {{ frustumCulled?: boolean, renderOrder?: number }} [opts]
   * @returns {THREE.Mesh[]}
   */
  addChunks(chunks, materialFor, { frustumCulled = true, renderOrder = 0 } = {}) {
    /** @type {THREE.Mesh[]} */
    const meshes = [];
    for (const c of chunks) {
      const mat = materialFor(c.matKey);
      const mesh = new THREE.Mesh(c.geometry, mat);
      mesh.name = `${c.matKey}@${c.cell.x},${c.cell.z}`;
      mesh.frustumCulled = frustumCulled;
      mesh.renderOrder = renderOrder;
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      this.add(mesh);
      meshes.push(mesh);
    }
    return meshes;
  }

  /**
   * Pushes four AABB colliders forming a wall around {@link bounds}, so an
   * actor cannot walk off the map into the fog skirt, and returns them.
   *
   * Every level so far hand-rolled this as a private `worldBounds(half)`
   * helper returning the same four literals (city with `h: 40`, wasteland with
   * `h: 60`, both with a 4 m thickness and a 0.5 m outward offset). This is
   * that helper, generalised to a rectangle instead of a half-extent.
   *
   * ```js
   * level.colliders = [...myColliders];
   * level.addWorldBounds(0.5);              // walls just outside the bounds
   * ```
   *
   * The walls are placed OUTSIDE `bounds` (they start at `bounds + margin` and
   * extend a further `thickness` outward), so `margin` is how much slack the
   * player gets past the last polygon — not how far in the wall intrudes.
   *
   * Call it AFTER assigning `this.colliders`; it appends, so an assignment
   * afterwards would drop the walls. Not retrofitted onto the three shipped
   * levels — they each still call their own private copy.
   *
   * @param {number} [margin=0] Metres of slack outside `bounds` before the wall.
   * @param {{thickness?: number, height?: number, tag?: string, y?: number}} [opts]
   *   `thickness` (default 4 m) is how deep the wall box is — it only has to
   *   exceed the fastest per-step displacement so nothing tunnels through.
   *   `height` (default 40 m) must clear anything an actor can stand on at the
   *   edge. `y` (default 0) is the wall's floor.
   * @returns {Collider[]} The four colliders that were appended.
   */
  addWorldBounds(margin = 0, { thickness = 4, height = 40, tag = 'boundary', y = 0 } = {}) {
    const b = this.bounds;
    const t = thickness;
    const x0 = b.minX - margin;
    const x1 = b.maxX + margin;
    const z0 = b.minZ - margin;
    const z1 = b.maxZ + margin;
    /** @type {Collider[]} */
    const walls = [
      // North and south run the full width INCLUDING the corners, so the two
      // side walls can stop at the corners and nothing leaks through a seam.
      { kind: 'aabb', minX: x0 - t, minZ: z0 - t, maxX: x1 + t, maxZ: z0, y, h: height, tag },
      { kind: 'aabb', minX: x0 - t, minZ: z1, maxX: x1 + t, maxZ: z1 + t, y, h: height, tag },
      { kind: 'aabb', minX: x0 - t, minZ: z0, maxX: x0, maxZ: z1, y, h: height, tag },
      { kind: 'aabb', minX: x1, minZ: z0, maxX: x1 + t, maxZ: z1, y, h: height, tag },
    ];
    this.colliders.push(...walls);
    return walls;
  }

  /**
   * Recomputes `meta.tris` / `meta.verts` / `meta.drawCalls` by walking `root`,
   * and stamps `buildMs`. Call it as the last line of a generator:
   * `return level.finalize();`
   * @returns {this}
   */
  finalize() {
    let tris = 0;
    let verts = 0;
    let draws = 0;
    this.root.traverse((o) => {
      const geo = /** @type {any} */ (o).geometry;
      if (!geo || !/** @type {any} */ (o).isMesh) return;
      const pos = geo.getAttribute?.('position');
      if (!pos) return;
      const n = geo.index ? geo.index.count : pos.count;
      const instances = /** @type {any} */ (o).isInstancedMesh ? /** @type {any} */ (o).count : 1;
      verts += n;
      tris += (n / 3) * instances;
      draws += 1;
    });
    this.meta.tris = Math.round(tris);
    this.meta.verts = verts;
    this.meta.drawCalls = draws;
    if (!this.meta.buildMs) this.meta.buildMs = now() - this._t0;
    return this;
  }

  // ── contract ───────────────────────────────────────────────────────────────

  /** Idempotent teardown. */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;

    try {
      this.root.removeFromParent();
    } catch {
      /* not parented */
    }

    for (const obj of this._owned) {
      try {
        obj?.dispose?.();
      } catch (err) {
        console.warn(`[level:${this.meta.id}] dispose failed`, err);
      }
    }
    this._owned.clear();

    try {
      this.decals?.dispose?.();
    } catch {
      /* noop */
    }
    try {
      this.probes?.dispose?.();
    } catch {
      /* noop */
    }
    this.decals = null;
    this.probes = null;

    // Materials/textures live in the registry; the Engine disposes it too, and
    // Registry.disposeAll is idempotent.
    try {
      this.registry?.disposeAll?.();
    } catch {
      /* noop */
    }

    this.root.clear();
  }
}

/** @returns {number} */
function now() {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}
